import { useState } from 'react'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { Command, CommandCollection, CommandInput, CommandItem, CommandList, CommandPanel } from '@w/ui/components/command'
import { Popover, PopoverTrigger, PopoverPopup, PopoverClose } from '@w/ui/components/popover'
import { Toggle } from '@w/ui/components/toggle'
import { CheckIcon, ChevronDownIcon } from '@w/ui/components/icon'
import { SpaceQueries } from '../db/spaces-queries'
import { usablePage } from '../db/page-eligibility'
import type { SpaceResource } from '../router/spaces-resources'
import { toTursoFtsQuery } from './page-search'

export type SearchPageScope = { pageId: string; title: string }
const pageSchema = s.object({ pageId: s.string(), title: s.string() })

export function WorkspaceSearchFilters({ resources, titleOnly, onTitleOnlyChange, selected, onSelectedChange }: {
  resources: SpaceResource[]
  titleOnly: boolean
  onTitleOnlyChange(this: void, value: boolean): void
  selected: SearchPageScope[]
  onSelectedChange(this: void, value: SearchPageScope[]): void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [query, setQuery] = useState('')
  return <div className="flex flex-wrap items-center gap-2 px-2 py-1">
    <Toggle size="sm" pressed={titleOnly} onPressedChange={onTitleOnlyChange} data-testid="workspace-search-title-only">Title only</Toggle>
    <Popover open={pickerOpen} onOpenChange={value => { setPickerOpen(value); if (value) setQuery('') }}>
      <PopoverTrigger data-testid="workspace-search-in" className="hover:bg-surface-2 flex min-h-8 items-center gap-1 rounded-md px-2 text-sm">
        <span>{selected.length === 0 ? 'In' : selected.length === 1 ? `In: ${selected[0].title.trim() || 'Untitled page'}` : `In: ${selected.length} pages`}</span><ChevronDownIcon className="size-4" />
      </PopoverTrigger>
      <PopoverPopup align="start" showArrow={false} className="w-72">
        {pickerOpen ? <SpaceQueries resources={resources} options={resource => scopePagesOptions(resource, query)}>
          {results => {
            const error = results.find(result => result.error)?.error
            const pages = [...new Map(results.flatMap(result => (result.data ?? []).map(page => [page.pageId, page] as const))).values()]
            return <Command items={pages} itemToStringValue={item => (item as SearchPageScope).title} mode="none" value={query} onValueChange={setQuery}>
              <CommandPanel>
                <CommandInput aria-label="Find pages to search in" placeholder="Search pages…" data-testid="workspace-search-in-query" />
                {error ? <p role="alert" className="text-error px-2 py-1 text-sm">Unable to load pages: {error.message}</p> : null}
                {!error && results.every(result => result.data !== undefined) && pages.length === 0 ? <p role="status" className="text-on-surface-secondary px-2 py-1 text-sm">No pages found</p> : null}
                <CommandList aria-multiselectable="true"><CommandCollection>{(page: SearchPageScope) => {
                  const checked = selected.some(item => item.pageId === page.pageId)
                  return <CommandItem key={page.pageId} value={page} aria-selected={checked} data-testid={`workspace-search-in-page-${page.pageId}`} onClick={() => {
                    onSelectedChange(checked ? selected.filter(item => item.pageId !== page.pageId) : [...selected, page])
                  }}>
                    <span className="min-w-0 flex-1 truncate">{page.title.trim() || 'Untitled page'}</span>
                    {checked ? <CheckIcon aria-label="Selected" className="size-4" /> : null}
                  </CommandItem>
                }}</CommandCollection></CommandList>
              </CommandPanel>
            </Command>
          }}
        </SpaceQueries> : null}
        <PopoverClose data-testid="workspace-search-in-done" className="hover:bg-surface-2 mt-2 rounded-md px-2 py-1 text-sm">Done</PopoverClose>
      </PopoverPopup>
    </Popover>
    {selected.length > 0 ? <button type="button" data-testid="workspace-search-in-clear" onClick={() => { onSelectedChange([]) }} className="text-on-surface-secondary hover:text-on-surface rounded-md px-2 py-1 text-sm">Clear In</button> : null}
  </div>
}

function scopePagesOptions(resource: SpaceResource, query: string) {
  const { db } = resource
  // fts_score/fts_match plan only on the indexed table alone, so the scored hits are a subquery joined to pages.
  if (query.trim()) return queryOptions(db.query`SELECT ${db.pages.id} AS pageId, ${db.pages.title}
      FROM (SELECT id, fts_score(title, text, ${toTursoFtsQuery(query, true)}) AS score FROM ${db.pageSearch} WHERE fts_match(title, text, ?1)
        ORDER BY score DESC LIMIT (SELECT count(*) FROM ${db.pageSearch})) hits
      JOIN ${db.pages} ON ${db.pages.id} = hits.id
      WHERE ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)}
      ORDER BY hits.score DESC, ${db.pages.title}, ${db.pages.id} LIMIT 20`, pageSchema)
  return queryOptions(db.query`SELECT ${db.pages.id} AS pageId, ${db.pages.title} FROM ${db.pages}
    WHERE ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)} ORDER BY ${db.pages.title}, ${db.pages.id} LIMIT 20`, pageSchema)
}
